import { useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import styles from "./ChatWidget.module.css";
import ChangeLanguage from "../ChangeLanguage";
import { getAccessToken, getPhoneNo } from "../../utils/storage";
import { cx } from "../../utils/utils";
import ChatSupportAPI from "../../services/chat-support-api";
import { RxCross1, RxHamburgerMenu } from "react-icons/rx";
import { MdSend } from "react-icons/md";

const chatSupportAPI = new ChatSupportAPI();

// The chat API expects full language names rather than i18next codes.
const API_LANGUAGE = { en: "english", hi: "hindi" };
const toApiLanguage = (code) => API_LANGUAGE[code] || "english";

// Matches the backend cap; anything longer is cut server-side.
const MAX_MESSAGE_LENGTH = 2000;

// Chat language buttons. Labels are always shown in their own script.
const LANGUAGE_OPTIONS = [
  { code: "en", label: "English", color: "#1048E6" },
  { code: "hi", label: "हिंदी", color: "#3B6FE8" },
];

// `value` is what the bot receives, so it stays in English in every language;
// only the button label and the user's bubble are translated.
// Order must match chatWidget.quickActions.text_1..text_N in the locale files.
const QUICK_ACTIONS = [
  "Loan Status",
  "EMI & Repayment",
  "NACH / Auto-Debit",
  "Loan Closure & NOC",
  "Refunds",
  "Cooling-Off Period",
  "Credit Bureau / CIBIL",
  "Re-Loan / Eligibility",
  "Customer Profile",
  "Technical Issues",
  "Payments & Transactions",
  "EMI & Interest Calculator",
  "Other",
].map((value, i) => ({
  value,
  labelKey: `chatWidget.quickActions.text_${i + 1}`,
}));

const LOAN_STATUS_CLASS = {
  Disbursed: styles.statusActive,
  Closed: styles.statusClosed,
};

// getPhoneNo() returns Number(localStorage value), so a missing phone comes
// back as 0 (or NaN if the stored value is malformed); treat those as empty.
function toPhonePayload(phone) {
  if (!phone) return null;
  const value = String(phone).trim();
  if (!value || value === "0") return null;
  return value;
}

export default function ChatWidget({ onClose }) {
  const { t, i18n } = useTranslation();
  const [identifying, setIdentifying] = useState(false);
  const [identifyError, setIdentifyError] = useState("");
  const [customer, setCustomer] = useState(null);

  // The chat language ("english" | "hindi") is kept twice on purpose:
  // - `language` state re-renders the chat, hiding the language buttons once
  //   a language is picked.
  // - `languageRef` is read by sendMessage and the header-switcher effect
  //   below, so they always get the latest value without re-running the
  //   effect whenever the language changes.
  // Always update both together via updateLanguage.
  const [language, setLanguage] = useState(null);
  const languageRef = useRef(null);
  function updateLanguage(lang) {
    languageRef.current = lang;
    setLanguage(lang);
  }

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [inputTooLong, setInputTooLong] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const scrollRef = useRef(null);

  const [docsLoading, setDocsLoading] = useState(false);

  function appendMessages(...newMessages) {
    setMessages((prev) => [...prev, ...newMessages]);
  }

  function buildChatPayload(message, conversationHistory, lang) {
    return {
      message,
      conversation_history: conversationHistory,
      email: customer?.email || customer?.inputEmail,
      phone: toPhonePayload(customer?.mobile || customer?.inputPhone),
      language: lang,
      token: getAccessToken() || "",
    };
  }

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, isTyping]);

  // Once a chat language has been picked, keep it in step with the header
  // language switcher so later bot requests follow the user's latest choice.
  useEffect(() => {
    if (!languageRef.current) return;
    updateLanguage(toApiLanguage(i18n.resolvedLanguage));
  }, [i18n.resolvedLanguage]);

  // Show language selection as the first chat message when customer is identified.
  const greetedRef = useRef(false);
  useEffect(() => {
    if (customer && !greetedRef.current) {
      greetedRef.current = true;
      // i18nKey makes the bubble render through <Trans> (for the <br/>);
      // content keeps a plain-text copy for the conversation history.
      setMessages([
        {
          role: "assistant",
          i18nKey: "chatWidget.text_6",
          content: t("chatWidget.text_6").replace(/<br\s*\/?>/g, "\n"),
          languageSelect: true,
        },
      ]);
    }
    // Only re-run when the customer is identified, not on language changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  async function fetchDocuments() {
    if (docsLoading) return;
    const requestMsg = { role: "user", content: t("chatWidget.text_7") };
    if (!customer?.customerID) {
      appendMessages(requestMsg, {
        role: "assistant",
        content: t("chatWidget.text_37"),
      });
      return;
    }
    setDocsLoading(true);
    try {
      const data = await chatSupportAPI.customerDocumentsAPI(
        customer?.customerID,
      );
      const loans = data?.documents || [];
      appendMessages(
        requestMsg,
        loans.length === 0
          ? { role: "assistant", content: t("chatWidget.text_8") }
          : {
              role: "assistant",
              content: t("chatWidget.text_9", { count: loans.length }),
              loanSelect: loans,
            },
      );
    } catch (error) {
      console.error("[ChatWidget] fetchDocuments failed", error);
      appendMessages({ role: "assistant", content: t("chatWidget.text_10") });
    } finally {
      setDocsLoading(false);
    }
  }

  function handleLoanSelect(loan) {
    const docs = loan?.documents || [];
    const msg =
      docs.length > 0
        ? t("chatWidget.text_11", { loanNo: loan?.loanNo, count: docs.length })
        : t("chatWidget.text_12", { loanNo: loan?.loanNo });
    appendMessages(
      { role: "user", content: String(loan?.loanNo) },
      { role: "assistant", content: msg, loanDocs: docs },
    );
  }

  async function downloadDocument(docId, docName) {
    try {
      const data = await chatSupportAPI.documentUrlAPI(docId);
      if (!data?.success || !data?.url) {
        Swal.fire({ icon: "error", text: t("chatWidget.text_13", { docName }) });
        return;
      }

      const url = new URL(data?.url);
      if (url.protocol !== "https:") {
        Swal.fire({ icon: "error", text: t("chatWidget.text_14") });
        return;
      }

      window.open(url.href, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("[ChatWidget] downloadDocument failed", error);
      Swal.fire({ icon: "error", text: t("chatWidget.text_15") });
    }
  }

  function handleLanguageSelect(code) {
    const lang = API_LANGUAGE[code];
    i18n.changeLanguage(code);
    updateLanguage(lang);
    const langLabel = LANGUAGE_OPTIONS.find((opt) => opt?.code === code)?.label;
    appendMessages({ role: "user", content: langLabel });
    autoGreet(lang);
  }

  async function autoGreet(lang) {
    setIsTyping(true);
    const greetMsg = lang === "hindi" ? "नमस्ते" : "hi";
    try {
      const data = await chatSupportAPI.sendChatMessageAPI(
        buildChatPayload(greetMsg, [], lang),
        { withAuth: false },
      );
      appendMessages({
        role: "assistant",
        content: data?.reply,
        options: data?.options || null,
      });
    } catch (error) {
      console.error("[ChatWidget] autoGreet failed", error);
      // Reset the language so the language buttons reappear and act as a retry.
      updateLanguage(null);
      appendMessages({
        role: "assistant",
        content: t("chatWidget.text_16"),
        languageSelect: true,
      });
    } finally {
      setIsTyping(false);
    }
  }

  async function fetchHistory() {
    if (identifying) return;
    setIdentifyError("");
    setIdentifying(true);

    try {
      const phone = getPhoneNo();
      const data = await chatSupportAPI.chatHistoryAPI({
        token: getAccessToken() || "",
        phone: toPhonePayload(phone),
      });

      if (data?.found) {
        // History exists: show it as-is and skip the default language-select greeting.
        greetedRef.current = true;
        setCustomer({ ...data?.customer, inputPhone: phone });
        const historyMessages = data?.messages || [];
        // A single history message means the conversation never got past the
        // language prompt, so surface the English/Hindi buttons on it just
        // like the fresh-conversation greeting does.
        const isLanguagePrompt = historyMessages.length === 1;
        // The conversation already got past the language prompt, so no
        // language buttons will show; use the app's current language for
        // the bot instead of sending it null.
        if (historyMessages.length > 1) {
          updateLanguage(toApiLanguage(i18n.resolvedLanguage));
        }
        setMessages(
          historyMessages.map((m, i) => ({
            role: m?.role,
            content: m?.content,
            languageSelect:
              isLanguagePrompt && i === historyMessages.length - 1,
            options: m?.options || null,
          })),
        );
      } else {
        // No history: proceed with the normal fresh-conversation greeting flow.
        setCustomer({ inputPhone: phone });
      }
    } catch (error) {
      console.error("[ChatWidget] fetchHistory failed", error);
      setIdentifyError(t("chatWidget.text_5"));
    } finally {
      setIdentifying(false);
    }
  }

  const historyFetchedRef = useRef(false);
  useEffect(() => {
    // Guards against React StrictMode's dev-only double-invocation of effects,
    // which would otherwise fire this fetch twice on first mount.
    if (historyFetchedRef.current) return;
    historyFetchedRef.current = true;
    fetchHistory();
    // Run once on mount; fetchHistory is recreated every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // displayText lets a quick action show its translated label in the user's
  // bubble while the bot still receives the English value.
  async function sendMessage(text, displayText) {
    const msg = (text || input).trim();
    if (!msg || isTyping || msg.length > MAX_MESSAGE_LENGTH) return;

    appendMessages({ role: "user", content: displayText || msg });
    setInput("");
    setInputTooLong(false);

    // Bot mode: send to /chat
    setIsTyping(true);
    const history = messages
      .map((m) => ({
        role: m?.role === "agent" ? "assistant" : m?.role,
        content: m?.content,
      }))
      .filter((m) => m.role === "user" || m.role === "assistant");

    try {
      const data = await chatSupportAPI.sendChatMessageAPI(
        buildChatPayload(msg, history, languageRef.current),
      );

      appendMessages({
        role: "assistant",
        content: data?.reply,
        options: data?.options || null,
        loanSelect: data?.documents?.length > 0 ? data?.documents : null,
      });
    } catch (error) {
      console.error("[ChatWidget] sendMessage failed", error);
      appendMessages({ role: "assistant", content: t("chatWidget.text_18") });
    } finally {
      setIsTyping(false);
    }
  }

  // Typed or pasted text past the limit is trimmed, and the warning stays
  // until the user brings the message back under the limit.
  function handleInputChange(e) {
    const value = e.target.value;
    if (value.length > MAX_MESSAGE_LENGTH) {
      setInput(value.slice(0, MAX_MESSAGE_LENGTH));
      setInputTooLong(true);
    } else {
      setInput(value);
      if (value.length < MAX_MESSAGE_LENGTH) setInputTooLong(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  // ======================== IDENTIFY SCREEN ========================
  if (!customer) {
    return (
      <div className={styles.page}>
        <div className={styles.identifyContainer}>
          <div className={styles.identifyLeft}>
            {onClose && (
              <button
                type="button"
                className={styles.identifyClose}
                onClick={onClose}
                aria-label={t("chatWidget.text_1")}
              >
                <RxCross1 size={16} />
              </button>
            )}
            <div className={styles.brandLogo}>RF</div>
            <h1 className={styles.brandName}>{t("chatWidget.text_36")}</h1>
            <p className={styles.brandTagline}>{t("chatWidget.text_2")}</p>
          </div>

          <div className={styles.identifyRight}>
            <div className={styles.identifyCard}>
              {identifyError ? (
                <>
                  <div className={styles.identifyError}>{identifyError}</div>
                  <button
                    className={styles.identifyBtn}
                    onClick={fetchHistory}
                    disabled={identifying}
                  >
                    {t("chatWidget.text_3")}
                  </button>
                </>
              ) : (
                <>
                  <span className={styles.spinner} />
                  <p className={styles.identifyText}>{t("chatWidget.text_4")}</p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
  // ======================== CHAT SCREEN ========================
  // Failed autoGreet retries append more language prompts; only the latest
  // one should show the English/Hindi buttons.
  let lastLanguageSelectIndex = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i]?.languageSelect) {
      lastLanguageSelectIndex = i;
      break;
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.chatContainer}>
        {sidebarOpen && (
          <div
            className={styles.sidebarOverlay}
            onClick={() => setSidebarOpen(false)}
          />
        )}
        <aside
          className={cx(styles.sidebar, sidebarOpen && styles.sidebarOpen)}
        >
          <div className={styles.sidebarBrand}>
            <div className={styles.avatarLg}>RF</div>
            <span className={styles.sidebarTitle}>{t("chatWidget.text_36")}</span>
          </div>
          <div className={styles.sidebarDivider} />
          <div className={styles.sidebarSection}>
            <div className={styles.sidebarLabel}>{t("chatWidget.text_19")}</div>
            {customer?.email && (
              <div className={styles.sidebarValue}>{customer?.email}</div>
            )}
            {customer?.mobile && (
              <div className={styles.sidebarValue}>{customer?.mobile}</div>
            )}
            {customer?.customerID && (
              <>
                <div className={styles.sidebarLabel} style={{ marginTop: 12 }}>
                  {t("chatWidget.text_20")}
                </div>
                <div className={styles.sidebarValue}>
                  {customer?.customerID}
                </div>
              </>
            )}
            {customer?.leadID && (
              <>
                <div className={styles.sidebarLabel} style={{ marginTop: 12 }}>
                  {t("chatWidget.text_21")}
                </div>
                <div className={styles.sidebarValue}>{customer?.leadID}</div>
              </>
            )}
          </div>
          <div className={styles.sidebarDivider} />
          <div className={styles.sidebarSection}>
            <button
              className={styles.sidebarActionBtn}
              style={{
                background: "#1048E6",
                color: "#fff",
                fontWeight: 600,
                marginBottom: 8,
                width: "100%",
              }}
              onClick={() => {
                setSidebarOpen(false);
                fetchDocuments();
              }}
              disabled={docsLoading || isTyping}
            >
              {docsLoading ? t("chatWidget.text_22") : t("chatWidget.text_23")}
            </button>
          </div>
          <div className={styles.sidebarDivider} />
          <div className={styles.sidebarSection}>
            <div className={styles.sidebarLabel}>{t("chatWidget.text_24")}</div>
            <div className={styles.sidebarActions}>
              {QUICK_ACTIONS.map((action) => (
                <button
                  key={action.value}
                  className={styles.sidebarActionBtn}
                  onClick={() => {
                    setSidebarOpen(false);
                    sendMessage(action.value, t(action.labelKey));
                  }}
                  disabled={isTyping}
                >
                  {t(action.labelKey)}
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* Main chat area */}
        <main className={styles.chatMain}>
          <header className={styles.header}>
            <button
              className={styles.menuBtn}
              onClick={() => setSidebarOpen((v) => !v)}
              aria-label={t("chatWidget.text_25")}
            >
              <RxHamburgerMenu size={20} />
            </button>
            <div className={styles.avatar}>RF</div>
            <div className={styles.headerInfo}>
              <div className={styles.headerTitle}>{t("chatWidget.text_27")}</div>
              <div className={styles.headerStatus}>
                <span className={styles.statusDot} />
                {t("chatWidget.text_29")}
              </div>
            </div>
            {/* <ChangeLanguage className={styles.langSelect} /> */}
            {onClose && (
              <button
                type="button"
                className={styles.headerClose}
                onClick={onClose}
                aria-label={t("chatWidget.text_1")}
              >
                <RxCross1 />
              </button>
            )}
          </header>

          <div className={styles.chat} ref={scrollRef}>
            {messages.map((m, i) => {
              const isAgent = m?.role === "agent";
              // Live-agent messages are styled as assistant messages.
              const roleClass = styles[isAgent ? "assistant" : m?.role];
              return (
                <div
                  key={`${m?.role}-${String(m?.content ?? "").substring(0, 20)}-${i}`}
                  className={cx(styles.row, roleClass)}
                >
                  {(m?.role === "assistant" || isAgent) && (
                    <div
                      className={cx(
                        styles.botAvatar,
                        isAgent && styles.agentAvatar,
                      )}
                    >
                      {isAgent ? "AG" : "RF"}
                    </div>
                  )}
                  {m?.role === "system" ? (
                    <div className={styles.systemMsg}>{m?.content}</div>
                  ) : (
                    <div
                      className={cx(
                        styles.bubble,
                        roleClass,
                        isAgent && styles.agentBubble,
                      )}
                    >
                      {m?.i18nKey ? (
                        <Trans i18nKey={m?.i18nKey} components={{ br: <br /> }} />
                      ) : (
                        m?.content
                      )}

                      {i === lastLanguageSelectIndex && !language && (
                        <div className={styles.quickOptions}>
                          {LANGUAGE_OPTIONS.map((opt) => (
                            <button
                              key={opt.code}
                              className={styles.quickOptBtn}
                              style={{
                                background: opt.color,
                                color: "#fff",
                                border: "none",
                              }}
                              onClick={() => handleLanguageSelect(opt.code)}
                              disabled={isTyping}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      )}

                      {m?.options?.length > 0 && (
                        <div className={styles.quickOptions}>
                          {m?.options?.map((opt) => (
                            <button
                              key={opt}
                              className={styles.quickOptBtn}
                              onClick={() => sendMessage(opt)}
                              disabled={isTyping}
                            >
                              {opt}
                            </button>
                          ))}
                        </div>
                      )}

                      {m?.loanSelect?.length > 0 && (
                        <div className={styles.quickOptions}>
                          {m?.loanSelect?.map((loan) => (
                            <button
                              key={loan?.loanNo}
                              className={styles.loanSelectBtn}
                              onClick={() => handleLoanSelect(loan)}
                              disabled={isTyping}
                            >
                              <div className={styles.loanSelectNo}>
                                {loan?.loanNo}
                              </div>
                              <div className={styles.loanSelectMeta}>
                                <span
                                  className={cx(
                                    styles.loanStatusBadge,
                                    LOAN_STATUS_CLASS[loan?.status] ||
                                      styles.statusOther,
                                  )}
                                >
                                  {loan?.status}
                                </span>
                                <span>
                                  {t("chatWidget.text_35", {
                                    amount:
                                      loan?.disbursalAmount ||
                                      loan?.loanAmtApproved,
                                  })}
                                </span>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}

                      {m?.loanDocs?.length > 0 && (
                        <div className={styles.documentsList}>
                          {m?.loanDocs?.map((doc, di) => {
                            const docId =
                              doc?.documentID || doc?.documentId || "";
                            const docType =
                              doc?.type ||
                              doc?.documentType ||
                              t("chatWidget.text_31");
                            return (
                              <div key={di} className={styles.documentCard}>
                                <div className={styles.documentInfo}>
                                  <span className={styles.documentIcon}>
                                    &#128196;
                                  </span>
                                  <div className={styles.documentName}>
                                    {docType}
                                  </div>
                                </div>
                                {docId && (
                                  <button
                                    className={styles.documentDownloadBtn}
                                    onClick={() =>
                                      downloadDocument(docId, docType)
                                    }
                                  >
                                    {t("chatWidget.text_30")}
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {isTyping && (
              <div className={cx(styles.row, styles.assistant)}>
                <div className={styles.botAvatar}>RF</div>
                <div
                  className={cx(styles.bubble, styles.assistant, styles.typing)}
                >
                  <span className={styles.dot} />
                  <span className={styles.dot} />
                  <span className={styles.dot} />
                </div>
              </div>
            )}
          </div>

          {inputTooLong && (
            <p className={styles.composerWarning} role="alert">
              {t("chatWidget.text_38", { count: MAX_MESSAGE_LENGTH })}
            </p>
          )}
          <div className={styles.composer}>
            <input
              className={styles.composerInput}
              type="text"
              placeholder={t("chatWidget.text_33")}
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              disabled={isTyping}
            />
            <button
              className={styles.sendBtn}
              onClick={() => sendMessage()}
              disabled={
                isTyping ||
                !input.trim() ||
                inputTooLong ||
                input.length > MAX_MESSAGE_LENGTH
              }
              title={t("chatWidget.text_34")}
            >
              <MdSend size={22} />
            </button>
          </div>
        </main>
      </div>
    </div>
  );
}
